// Builds an application route from a dashboard path template and record identifier(s).
// dashboard routes may include template placeholders like `${booking_id}` which we
// replace with values found on `id`. The `id` string can either be a simple scalar,
// a JSON object (e.g. `{ "booking_id": 42 }`) or a query string style payload
// (`booking_id=42&session_id=10`). We fall back to appending the raw id when no
// placeholders are present in the route template.
export function fLinkBuild(route: string, id?: unknown) {
  if (!route) return '/';

  const cleanRoute = route.replace(/^\/+/, '');
  const placeholderPattern = /\$\{([^}]+)\}/g;
  const hasPlaceholders = /\$\{[^}]+\}/.test(cleanRoute);

  const rawId =
    id == null
      ? ''
      : typeof id === 'string'
      ? id
      : typeof id === 'number' || typeof id === 'boolean'
      ? String(id)
      : JSON.stringify(id);

  const trimmedId = rawId.trim();
  let replacementMap: Record<string, string> = {};
  let fallbackId: string | undefined;

  if (trimmedId) {
    if (
      (trimmedId.startsWith('{') && trimmedId.endsWith('}')) ||
      (trimmedId.startsWith('[') && trimmedId.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(trimmedId);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          Object.entries(parsed).forEach(([key, value]) => {
            replacementMap[key] = String(value);
          });
          const values = Object.values(replacementMap);
          if (values.length === 1) fallbackId = values[0];
        }
      } catch (error) {
        console.error('Failed to parse notification record id JSON', error);
      }
    } else if (trimmedId.includes('=')) {
      trimmedId.split('&').forEach((pair) => {
        const [key, value] = pair.split('=');
        if (key && value !== undefined) {
          replacementMap[key] = decodeURIComponent(value);
        }
      });
      const values = Object.values(replacementMap);
      if (values.length === 1) fallbackId = values[0];
    } else {
      fallbackId = trimmedId;
    }
  }

  const lookupReplacement = (key: string) => {
    const normalisedKey = key.trim();
    const direct = normalisedKey in replacementMap ? replacementMap[normalisedKey] : undefined;
    const value = direct ?? fallbackId;
    return value ?? '';
  };

  let path = `/${cleanRoute}`;

  path = path.replace(placeholderPattern, (_, key) => lookupReplacement(key));

  if (!hasPlaceholders) {
    const valueToAppend = fallbackId ?? trimmedId;
    if (valueToAppend) {
      path = `${path}/${valueToAppend.replace(/^\/+/, '')}`;
    }
  }

  // remove any unresolved placeholders to avoid leaking template syntax
  path = path.replace(placeholderPattern, '');

  return path.replace(/\/+/g, '/');
}

export function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}
